import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import {LoadingButton} from '@mui/lab';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip as TooltipRecharts,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {usePrint} from '../../../hooks/print';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {
  DashboardPanelData,
  PanelFilterParams,
} from '../../../interfaces/Dashboard';
import {
  MachineDetailBarReportQuery,
  MachineDetailBarReportResponse,
} from '../../../interfaces/MachineDetailBarReport';
import reduxSelectors from '../../../redux/selectors';
import {saveFile} from '../../../utils/file';
import {isPresent} from '../../../utils/type-guards';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DraggablePagination from '../../common/DraggablePagination';
import {useDashboardDrilldown} from '../../dashboards/DashboardContext';
import {usePanelFilter} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {MachineInputSelectV2} from '../../selectors/MachineInputSelectV2';
import {MachineTypeSelect} from '../../selectors/MachineTypeSelect';
import {ShiftSelect} from '../../selectors/ShiftSelect';
import {ZoneSelect} from '../../selectors/ZoneSelect';
import DrilldownMenu, {MenuState} from './DrilldownMenu';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value: DashboardPanelData) => void;
}

const MachineDetailsReportBar = (props: Props) => {
  const {filter, setFilter} = usePanelFilter();

  /*************/
  /* pagination */
  /*************/
  const [page, setPage] = useState<number>(
    filter.page ?? props.value?.page ?? 0
  );
  const [limit, setLimit] = useState<number>(
    filter.limit ?? props.value?.limit ?? 20
  );
  const [position, setPosition] = useState<
    {x: number; y: number} | undefined
  >(filter.position ?? props.value?.position ?? undefined);

  /*********/
  /* fetch */
  /*********/

  const [fetchedData, setFetchedData] =
    useState<MachineDetailBarReportResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const params = useMemo<MachineDetailBarReportQuery>(() => {
    const filterParams = filter.params ?? {};
    const propsParams = props.value?.params ?? {};

    return {
      type_id: 'type_id' in filterParams ? filterParams.type_id : propsParams.type_id ?? null,
      shift_id: 'shift_id' in filterParams ? filterParams.shift_id : propsParams.shift_id ?? null,
      zone_id: 'zone_id' in filterParams ? filterParams.zone_id : propsParams.zone_id ?? null,
      date_start: filterParams.date_start ?? propsParams.date_start ?? dayjs().format('YYYY-MM-DD'),
      date_end: filterParams.date_end ?? propsParams.date_end ?? dayjs().format('YYYY-MM-DD'),
      input: filterParams.input ?? propsParams.input ?? {},
      page,
      limit,
    };
  }, [filter.params, page, limit]);

  const fetchData = useCallback(
    async (params: MachineDetailBarReportQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/report/machine-details-bar`;
        const resp = await API.get<MachineDetailBarReportResponse>(endpoint, {
          params: {
            ...params,
            input: JSON.stringify(params.input),
          },
        });
        setFetchedData(resp.data);
        setHasPendingChanges(false);
        setIsFirstLoad(false);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, "STRA_abe3adea");
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  const showPendingChangesWarning = useMemo(
    () => hasPendingChanges && filter.autoReload === false,
    [hasPendingChanges, filter.autoReload]
  );

  const handleRefresh = useCallback(() => {
    if (params) {
      fetchData(params);
      setHasPendingChanges(false);
    }
  }, [params, fetchData]);

  const handleToggleAutoReload = useCallback(() => {
    const isCurrentlyEnabled = filter.autoReload !== false;
    setFilter(
      update(filter, {
        autoReload: {
          $set: !isCurrentlyEnabled,
        },
      })
    );
  }, [filter, setFilter]);

  useEffect(() => {
    if (params) {
      if (filter.autoReload !== false || isFirstLoad) {
        fetchData(params);
      } else if (!isFirstLoad) {
        setHasPendingChanges(true);
      }
      props.onUpdate?.({...props.value, chartType: 'bar', params, autoReload: filter.autoReload});
    }
  }, [params, filter.autoReload]);

  /****************/
  /* auto refresh */
  /****************/
  const [refreshInterval, setRefreshInterval] = useRefreshInterval(
    () => fetchData(params),
    props.value?.refreshInterval
  );

  useEffect(() => {
    props.onUpdate?.({...props.value, chartType: 'bar', refreshInterval});
  }, [refreshInterval]);

  useEffect(() => {
    if (filter.page !== undefined && filter.page !== page) {
      setPage(filter.page);
    }
    if (filter.limit !== undefined && filter.limit !== limit) {
      setLimit(filter.limit);
    }
    if (filter.position !== undefined) {
      setPosition(filter.position);
    }
  }, [filter.page, filter.limit, filter.position]);

  useEffect(() => {
    setFilter({
      ...filter,
      page,
      limit,
      position,
    });
    props.onUpdate?.({
      ...props.value,
      page,
      limit,
      position,
    });
  }, [page, limit, position]);

  useEffect(() => {
    setPage(0);
  }, [params.type_id, params.shift_id, params.zone_id, params.date_start, params.date_end, params.input]);

  /*********/
  /* chart */
  /*********/
  const theme = useTheme();

  const dataset = useMemo(() => {
    return fetchedData?.results?.map((i) => ({
      name: i.machine.name,
      warnings: i.interactions.warnings,
      hazards: i.interactions.hazard,
      remote_stops: i.interactions.remote_stops,
      external_id: i.machine.external_id,
    }));
  }, [fetchedData?.results]);

  const bars = [
    {
      key: 'warnings',
      label: 'Warning',
      color: 'yellow',
    },
    {
      key: 'hazards',
      label: 'Hazard',
      color: 'red',
    },
    {
      key: 'remote_stops',
      label: 'Remote Stop',
      color: 'purple',
    },
  ];

  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
  const toggleKey = (key: string) => {
    setHiddenKeys(
      hiddenKeys.includes(key)
        ? hiddenKeys.filter((i) => i !== key)
        : [...hiddenKeys, key]
    );
  };

  /*********/
  /* print */
  /*********/
  const chartRef = useRef<HTMLDivElement>();
  const [printChart] = usePrint(chartRef.current);
  const printTheme = useAppSelector(({app}) => app.printTheme);

  /**********/
  /* export */
  /**********/
  const [exportInProgress, setExportInProgress] = useState(false);

  const {
    shifts,
    zones,
    machine_types: machineTypes,
    machine_inputs: machineInputs,
  } = useAppSelector(reduxSelectors.assets.getAssets);

  const submitExport = async (params: MachineDetailBarReportQuery) => {
    setExportInProgress(true);
    try {
      const endpoint = `${apiBaseUrl}/report/machine-details-export`;
      const resp = await API.get(endpoint, {
        params: {
          ...params,
          input: JSON.stringify(params.input),
          export_type: 'bar',
        },
        responseType: 'blob',
      });

      const fileNameParts = [
        'machineDetailsBar',
        `${params.date_start}__${params.date_end}`,
        shifts.find((i) => i.id === params.shift_id)?.name?.replace(' ', '-'),
        zones.find((i) => i.id === params.zone_id)?.name?.replace(' ', '-'),
        machineTypes
          .find((i) => i.id === params.type_id)
          ?.name?.replace(' ', '-'),

        machineInputs
          .map((i) => {
            const v = params.input?.[i.id];
            if (v === 1) {
              return `${i.name.replace(' ', '-')}-is-on`;
            } else if (v === 0) {
              return `${i.name.replace(' ', '-')}-is-off`;
            }
          })
          .filter(isPresent)
          .join('_'),
      ].filter(isPresent);

      const fileName = `${fileNameParts.join('__')}.csv`;

      saveFile(resp.data, fileName);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_0c358411");
      setFetchedErrors(messages);
    }
    setExportInProgress(false);
  };

  const drilldown = useDashboardDrilldown();
  const [menuState, setMenuState] = useState<MenuState>({
    visible: false,
    mouseX: 0,
    mouseY: 0,
  });

  const openDrilldown = (externalId: number, event: any) => {
    setMenuState?.(
      update(menuState, {
        visible: {$set: true},
        mouseY: {$set: event.clientY},
        mouseX: {$set: event.clientX},
        params: {
          $set: {
            ...params,
            machines: [externalId],
            external_id: externalId,
            section_id: params.zone_id,
          },
        },
      })
    );
  };

  const handleDrilldown = (
    state: MenuState,
    type: 'history' | 'interactions' | 'summary'
  ) => {
    if (type === 'history') {
      drilldown({
        code: 'MachineHistoryReport',
        chartType: 'bar',
        filter: state.params as PanelFilterParams,
      });
    } else if (type === 'interactions') {
      drilldown({
        code: 'MachineInteractionReport',
        chartType: 'bar',
        filter: state.params as PanelFilterParams,
      });
    } else {
      drilldown({
        code: 'MachineSummaryReport',
        chartType: 'bar',
        filter: state.params as PanelFilterParams,
      });
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      gap={1}
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" flexDirection="column">
        <DrilldownMenu
          onItemClick={handleDrilldown}
          menuState={menuState}
          setMenuState={setMenuState}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="start"
            gap={1}
          >
            <Box display="flex" overflow="auto" py={1} gap={1}>
              <MachineTypeSelect
                value={params.type_id}
                size="small"
                sx={{minWidth: 200}}
                onChange={(v) => {
                  setFilter(
                    update(filter, {
                      params: {
                        type_id: {
                          $set: v,
                        },
                      },
                    })
                  );
                }}
              />

              <Box minWidth={400}>
                <DateRangeSelect
                  value={[
                    dayjs(params.date_start).toDate(),
                    dayjs(params.date_end).toDate(),
                  ]}
                  size="small"
                  onChange={(v) => {
                    setFilter(
                      update(filter, {
                        params: {
                          date_start: {
                            $set: v?.[0]
                              ? dayjs(v?.[0]).format('YYYY-MM-DD')
                              : undefined,
                          },
                          date_end: {
                            $set: v?.[1]
                              ? dayjs(v?.[1]).format('YYYY-MM-DD')
                              : undefined,
                          },
                        },
                      })
                    );
                  }}
                />
              </Box>

              <ShiftSelect
                value={params?.shift_id}
                size="small"
                nullLabel="All Shifts"
                sx={{minWidth: 200}}
                onChange={(v) => {
                  setFilter(
                    update(filter, {
                      params: {
                        shift_id: {
                          $set: v,
                        },
                      },
                    })
                  );
                }}
              />

              <ZoneSelect
                value={params?.zone_id}
                size="small"
                nullLabel="All Sections"
                sx={{minWidth: 200}}
                onChange={(v) => {
                  setFilter(
                    update(filter, {
                      params: {
                        zone_id: {
                          $set: v,
                        },
                      },
                    })
                  );
                }}
              />

              <Box width={250}>
                <MachineInputSelectV2
                  value={params?.input}
                  label="Inputs"
                  size="small"
                  onChange={(v) => {
                    setFilter(
                      update(filter, {
                        params: {
                          input: {
                            $set: v,
                          },
                        },
                      })
                    );
                  }}
                />
              </Box>
            </Box>

            <Box display="flex" py={1}>
              <ButtonGroup size="small" sx={{height: 40}}>
                <Tooltip title="Reload">
                  <Button onClick={handleRefresh}>
                    <RefreshIcon />
                  </Button>
                </Tooltip>

                <Tooltip
                  title={
                    filter.autoReload === false
                      ? 'Auto-reload disabled: Use refresh button to apply filter changes'
                      : 'Auto-reload enabled: Report reloads when filters change'
                  }
                >
                  <Button
                    size="small"
                    variant={
                      filter.autoReload === false ? 'outlined' : 'contained'
                    }
                    onClick={handleToggleAutoReload}
                    sx={{minWidth: 'auto'}}
                  >
                    <SyncIcon />
                  </Button>
                </Tooltip>

                <AutoRefreshSelect
                  value={props.value?.refreshInterval}
                  onChange={setRefreshInterval}
                />

                <Tooltip title="Export to Excel">
                  <LoadingButton
                    size="small"
                    loading={exportInProgress}
                    variant="outlined"
                    onClick={() => submitExport(params)}
                  >
                    <FileDownloadIcon />
                  </LoadingButton>
                </Tooltip>

                <Tooltip title="Print">
                  <Button onClick={() => printChart()}>
                    <PrintIcon />
                  </Button>
                </Tooltip>
              </ButtonGroup>
            </Box>
          </Box>
        </DrilldownMenu>
      </Box>

      {showPendingChangesWarning && (
        <Alert severity="warning" sx={{mb: 1}}>
          Auto-reload is disabled. Click the refresh button to apply filter
          changes.
        </Alert>
      )}

      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}
      <Box
        ref={chartRef}
        overflow="hidden"
        position="relative"
        display="flex"
        flexDirection="column"
        gap={3}
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%"
      >
        <DraggablePagination
          page={page}
          pageSize={limit}
          totalItems={fetchedData?.total ?? 0}
          position={position}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          onPositionChange={setPosition}
        />

        <Box display="none" displayPrint="block">
          Zones of interest for Machines - {params.date_start} to{' '}
          {params.date_end}
        </Box>

        <Box
          height={printTheme ? '500px' : '100%'}
          width={printTheme ? '700px' : '100%'}
        >
          {fetchedData?.results?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dataset}>
                <XAxis dataKey="name" />
                <YAxis />
                <TooltipRecharts
                  shared={false}
                  labelStyle={{
                    color: theme.palette.text.secondary,
                  }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.default,
                  }}
                  cursor={false}
                />
                <Legend onClick={(item) => toggleKey(item.dataKey)} />

                {bars.map(({key, label, color}) => (
                  <Bar
                    onClick={(data, _n, event) =>
                      openDrilldown(data.external_id, event)
                    }
                    key={key}
                    dataKey={key}
                    name={label}
                    hide={hiddenKeys.includes(key)}
                    label={{
                      position: 'top',
                      fill: theme.palette.text.secondary,
                    }}
                    // fill={CHART_COLORS[index]}
                    fill={color}
                    isAnimationActive={!printTheme}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              display="flex"
              height="100%"
              alignItems="center"
              justifyContent="center"
            >
              {fetchedInProgress ? 'Loading...' : 'No data to display'}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MachineDetailsReportBar;
